import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { FiUpload, FiX, FiFile, FiDownload, FiTrash2, FiUser, FiCheckCircle, FiEye } from 'react-icons/fi';
import jsPDF from 'jspdf';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getGuardsWithDocuments,
  addGuardDocument,
  deleteGuardDocument,
  GuardWithDocuments,
  GuardDocument,
} from '../service/documentService';
import PDFViewerModal from './PDFViewerModal';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const GuardSection = styled.div`
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const GuardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
`;

const GuardImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${theme.colors.light.border.medium};
`;

const GuardInfo = styled.div`
  flex: 1;
`;

const GuardName = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const GuardDetails = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const DocumentStatus = styled.div<{ hasDocuments: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${({ hasDocuments }) =>
    hasDocuments ? theme.colors.accent.success : theme.colors.accent.warning};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const UploadSection = styled.div`
  margin-bottom: ${theme.spacing[3]};
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background-color: ${theme.colors.primary[500]};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  height: 40px;
  white-space: nowrap;

  &:hover {
    background-color: ${theme.colors.primary[600]};
  }

  input {
    display: none;
  }
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]};
  background-color: ${theme.colors.light.background.secondary};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex: 1;
`;

const DocumentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${theme.colors.primary[50]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[600]};
`;

const DocumentDetails = styled.div`
  flex: 1;
`;

const DocumentName = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const DocumentMeta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const DocumentActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.light.text.primary};
  }

  &.delete:hover {
    color: ${theme.colors.accent.danger};
    background-color: ${theme.colors.accent.danger}20;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[4]};
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const FileInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[2]};
  align-items: flex-start;
`;

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 0;
  gap: ${theme.spacing[2]};
`;

interface GuardDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientPath: string;
  clientName: string;
  onDocumentsUpdated?: () => void;
}

const GuardDocumentsModal: React.FC<GuardDocumentsModalProps> = ({
  isOpen,
  onClose,
  clientPath,
  clientName,
  onDocumentsUpdated,
}) => {
  const { companyData } = useAuth();
  const [guards, setGuards] = useState<GuardWithDocuments[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<{ [guardId: string]: boolean }>({});
  const [deleting, setDeleting] = useState<{ [documentId: string]: boolean }>({});
  const [documentNames, setDocumentNames] = useState<{ [key: string]: string }>({});
  const [viewingPDF, setViewingPDF] = useState<{ url: string; name: string; size?: number; uploadedAt?: Date | string } | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadGuards();
    }
  }, [isOpen, clientPath]);

  const loadGuards = async () => {
    setLoading(true);
    try {
      const guardsData = await getGuardsWithDocuments(clientPath);
      setGuards(guardsData);
    } catch (error: any) {
      console.error('Error loading guards:', error);
      toast.error('Failed to load guards');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (guardId: string, file: File) => {
    if (!file) return;

    // Validate file type - only allow PDF
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }

    const documentName = documentNames[guardId] || file.name;
    if (!documentName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setUploading((prev) => ({ ...prev, [guardId]: true }));
    try {
      await addGuardDocument(clientPath, guardId, file, documentName);
      toast.success('Document uploaded successfully');
      setDocumentNames((prev) => ({ ...prev, [guardId]: '' }));
      await loadGuards();
      if (onDocumentsUpdated) {
        onDocumentsUpdated();
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploading((prev) => ({ ...prev, [guardId]: false }));
    }
  };

  const handleDeleteDocument = async (guardId: string, documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeleting((prev) => ({ ...prev, [documentId]: true }));
    try {
      await deleteGuardDocument(clientPath, guardId, documentId);
      toast.success('Document deleted successfully');
      await loadGuards();
      if (onDocumentsUpdated) {
        onDocumentsUpdated();
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error(error.message || 'Failed to delete document');
    } finally {
      setDeleting((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (date: Date | string | any): string => {
    if (!date) return 'N/A';

    let d: Date;

    // Handle Firestore Timestamp
    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      d = date.toDate();
    }
    // Handle string
    else if (typeof date === 'string') {
      d = new Date(date);
      if (isNaN(d.getTime())) return 'Invalid Date';
    }
    // Handle Date object
    else if (date instanceof Date) {
      d = date;
    }
    // Handle object with seconds/nanoseconds (Firestore Timestamp structure)
    else if (date && typeof date === 'object' && (date.seconds || date._seconds)) {
      const seconds = date.seconds || date._seconds || 0;
      const nanoseconds = date.nanoseconds || date._nanoseconds || 0;
      d = new Date(seconds * 1000 + nanoseconds / 1000000);
    }
    else {
      return 'Invalid Date';
    }

    if (isNaN(d.getTime())) {
      return 'Invalid Date';
    }

    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const loadImageAsDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const generateDeploymentLetterPdf = async (guard: GuardWithDocuments): Promise<Blob> => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    let yPos = 20;

    const companyName =
      (companyData as any)?.companyName ||
      (companyData as any)?.name ||
      'Security Company';

    const companyLogo =
      (companyData as any)?.companyImg ||
      (companyData as any)?.logo ||
      (companyData as any)?.logoUrl ||
      undefined;

    const companyAddress =
      (companyData as any)?.address ||
      (companyData as any)?.headOfficeAddress ||
      (companyData as any)?.officeAddress ||
      (companyData as any)?.companyAddress ||
      '';

    const companyPhone =
      (companyData as any)?.companyNo || // common pattern: companyNo used as main phone number
      (companyData as any)?.phone ||
      (companyData as any)?.companyPhone ||
      (companyData as any)?.contactNumber ||
      (companyData as any)?.phoneNumber ||
      '';

    const companyEmail =
      (companyData as any)?.companyEmail ||
      (companyData as any)?.email ||
      (companyData as any)?.primaryEmail ||
      '';

    const companyWebsite =
      (companyData as any)?.companyWebsite ||
      (companyData as any)?.website ||
      (companyData as any)?.web ||
      (companyData as any)?.site ||
      '';

    // Preload logo once for watermark and header
    let logoDataUrl: string | null = null;
    if (companyLogo) {
      try {
        logoDataUrl = await loadImageAsDataUrl(companyLogo);
      } catch (error) {
        console.warn('Failed to load company logo', error);
      }
    }

    // Center watermark logo with low opacity (memo style) if logo available
    if (logoDataUrl) {
      try {
        const watermarkWidth = pageWidth * 0.6;
        const watermarkHeight = watermarkWidth * 0.5;
        const watermarkX = (pageWidth - watermarkWidth) / 2;
        const watermarkY = (pageHeight - watermarkHeight) / 2;

        const anyPdf = pdf as any;
        if (anyPdf.GState && anyPdf.setGState) {
          const transparentState = new anyPdf.GState({ opacity: 0.04 });
          anyPdf.setGState(transparentState);
          pdf.addImage(logoDataUrl, 'PNG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
          const normalState = new anyPdf.GState({ opacity: 1 });
          anyPdf.setGState(normalState);
        } else {
          // Fallback without opacity control
          pdf.addImage(logoDataUrl, 'PNG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
        }
      } catch (error) {
        console.warn('Failed to draw watermark logo', error);
      }
    }

    // Header logo (top-left)
    if (logoDataUrl) {
      try {
        const logoWidth = 46; // mm
        const logoHeight = 18; // mm
        pdf.addImage(logoDataUrl, 'PNG', 15, 10, logoWidth, logoHeight);
      } catch (error) {
        console.warn('Failed to load company logo for PDF header', error);
      }
    }

    // Company name (top center)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(25);
    pdf.text(companyName.toUpperCase(), pageWidth - 15, 22, { align: 'right' });

    // Divider line below header
    pdf.setLineWidth(0.3);
    pdf.line(15, 30, pageWidth - 15, 30);

    // Body vertical start roughly centered on the page (below header)
    const bodyTop = Math.max(80, pageHeight / 2 - 85);

    // Date as part of body (right-aligned above "To" block)
    const today = new Date();
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(`Date: ${today.toLocaleDateString()}`, pageWidth - 20, bodyTop - 6, { align: 'right' });

    yPos = bodyTop;

    const lineHeight = 6;

    // "To" block - centered body content with client lines
    pdf.setFontSize(12);
    const toLines: string[] = [
      'To,',
      '',
      'Respected Client',
      clientName || 'Client',
      'Branch Karachi',
    ];
    toLines.forEach((line) => {
      if (line.trim().length > 0) {
        pdf.text(line, 20, yPos);
      }
      yPos += lineHeight;
    });

    yPos += 6;

    // Subject
    pdf.setFont('helvetica', 'bold');
    pdf.text('Subject:  DEPLOYMENT OF SECURITY STAFF', 20, yPos);
    yPos += 12;

    // Body
    pdf.setFont('helvetica', 'normal');
    const bodyLines = [
      'Dear Sir,',
      '',
      'We are sure very hopeful that the services of our guard at your location prove to be forerunner.',
      'The Security staff which is delpoy at your PREMISES',
      '',
      'Under:',
    ];
    bodyLines.forEach((line) => {
      const wrapped = pdf.splitTextToSize(line, pageWidth - 40);
      wrapped.forEach((wLine: string) => {
        pdf.text(wLine, 20, yPos);
        yPos += lineHeight;
      });
    });

    yPos += 2;

    // Guard detail line with dynamic name & CNIC
    const guardLine = `${guard.name || guard.guardId || 'Guard'} (CNIC: ${guard.cnic || 'N/A'})`;
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${guardLine}`, 25, yPos);
    yPos += lineHeight * 2;

    // Closing
    pdf.setFont('helvetica', 'normal');
    const closingLines = [
      'You are requested to kindly acknowledge the deployment of our security staff.',
      '',
      'Thanking you,',
      'Yours faithfully,',
      '',
    ];

    closingLines.forEach((line) => {
      const wrapped = pdf.splitTextToSize(line, pageWidth - 40);
      wrapped.forEach((wLine: string) => {
        pdf.text(wLine, 20, yPos);
        yPos += lineHeight;
      });
    });

    // Signature block (bottom-right area)
    const signatureY = pageHeight - 60;

    // "Computarized Letter" stamp above signature - straight, with blue border
    const stampText = 'COMPUTARIZED LETTER';
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 102, 204); // blue

    // Calculate approximate text width to size the stamp box
    const textWidth =
      (pdf.getStringUnitWidth(stampText) * pdf.getFontSize()) /
      (pdf as any).internal.scaleFactor;
    const paddingX = 4;
    const paddingY = 2;
    const stampWidth = textWidth + paddingX * 2;
    const stampHeight = 8 + paddingY; // slightly taller for stamp look

    const stampX = pageWidth - 80; // left of stamp box
    const stampY = signatureY - stampHeight - 6; // above signature line

    // Draw stamp border
    pdf.setLineWidth(0.4);
    pdf.setDrawColor(0, 102, 204);
    pdf.rect(stampX, stampY, stampWidth, stampHeight);

    // Draw stamp text centered inside
    const textX = stampX + stampWidth / 2;
    const textY = stampY + stampHeight / 2 + 2; // vertical centering tweak
    pdf.text(stampText, textX, textY, { align: 'center' });

    // Reset text color for signature
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    pdf.text('_________________________', pageWidth - 80, signatureY);
    pdf.text('Authorized Signatory', pageWidth - 80, signatureY + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.text(companyName, pageWidth - 80, signatureY + 12);

    // Company footer: address line, then contact line (phone, email, website) at the very bottom center
    const footerLines: string[] = [];

    if (companyAddress) {
      footerLines.push(`Head Office: ${companyAddress}`);
    }

    const contactParts: string[] = [];
    if (companyPhone) contactParts.push(`Ph: ${companyPhone}`);
    if (companyEmail) contactParts.push(`Email: ${companyEmail}`);
    if (companyWebsite) contactParts.push(`Web: ${companyWebsite}`);

    if (contactParts.length > 0) {
      footerLines.push(contactParts.join('    '));
    }

    if (footerLines.length > 0) {
      pdf.setFontSize(9);
      // Compute first footer line Y position
      const firstLineY = pageHeight - 15 - (footerLines.length - 1) * 4;

      // Divider line above footer
      const footerLineY = firstLineY - 4;
      pdf.setLineWidth(0.3);
      pdf.line(15, footerLineY, pageWidth - 15, footerLineY);

      let footerY = firstLineY;
      footerLines.forEach((line: string) => {
        pdf.text(line, pageWidth / 2, footerY, { align: 'center' });
        footerY += 4;
      });
    }

    return pdf.output('blob');
  };

  const handleGenerateDeploymentLetter = async (guard: GuardWithDocuments) => {
    const guardKey = `${guard.id}-${guard.guardId}`;
    const documentName =
      documentNames[guardKey] ||
      `Deployment Letter - ${guard.name || guard.guardId || 'Guard'}`;

    setUploading((prev) => ({ ...prev, [guard.id]: true }));
    try {
      const pdfBlob = await generateDeploymentLetterPdf(guard);
      const fileName = `${documentName}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      await addGuardDocument(clientPath, guard.id, pdfFile, documentName);
      toast.success('Deployment letter generated and attached successfully');
      setDocumentNames((prev) => ({ ...prev, [guardKey]: '' }));
      await loadGuards();
      if (onDocumentsUpdated) {
        onDocumentsUpdated();
      }
    } catch (error: any) {
      console.error('Error generating deployment letter:', error);
      toast.error(error.message || 'Failed to generate deployment letter');
    } finally {
      setUploading((prev) => ({ ...prev, [guard.id]: false }));
    }
  };

  // Preview deployment letter without saving (development only)
  const handlePreviewDeploymentLetter = async (guard: GuardWithDocuments) => {
    try {
      const pdfBlob = await generateDeploymentLetterPdf(guard);
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPreviewObjectUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return blobUrl;
      });
      const guardKey = `${guard.id}-${guard.guardId}`;
      const documentName =
        documentNames[guardKey] ||
        `Deployment Letter - ${guard.name || guard.guardId || 'Guard'}`;

      setViewingPDF({
        url: blobUrl,
        name: `Preview - ${documentName}`,
        size: pdfBlob.size,
        uploadedAt: new Date(),
      });
    } catch (error: any) {
      console.error('Error generating preview deployment letter:', error);
      toast.error(error.message || 'Failed to preview deployment letter');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Guard Documents - ${clientName}`}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <ModalContent>
        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>
            Loading guards...
          </div>
        ) : guards.length === 0 ? (
          <EmptyState>No guards deployed to this client</EmptyState>
        ) : (
          guards.map((guard) => {
            const hasDocuments = guard.documents && guard.documents.length > 0;
            const guardKey = `${guard.id}-${guard.guardId}`;
            const fileInputId = `file-input-${guard.id}`;

            return (
              <GuardSection key={guard.id}>
                <GuardHeader>
                  {guard.image ? (
                    <GuardImage src={guard.image} alt={guard.name} />
                  ) : (
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: theme.colors.light.background.tertiary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.light.text.muted,
                      }}
                    >
                      <FiUser size={24} />
                    </div>
                  )}
                  <GuardInfo>
                    <GuardName>
                      {guard.name || guard.guardId} {guard.slotNumber && `(Slot ${guard.slotNumber})`}
                    </GuardName>
                    <GuardDetails>
                      CNIC: {guard.cnic || '—'} | Guard ID: {guard.guardId}
                    </GuardDetails>
                  </GuardInfo>
                  <DocumentStatus hasDocuments={!!hasDocuments}>
                    {hasDocuments ? (
                      <>
                        <FiCheckCircle size={18} />
                        <span>{guard.documents?.length || 0} document(s)</span>
                      </>
                    ) : (
                      <>
                        <FiX size={18} />
                        <span>Pending documents</span>
                      </>
                    )}
                  </DocumentStatus>
                </GuardHeader>

                <UploadSection>
                  <FileInputContainer>
                    <InputWrapper>
                      <Input
                        placeholder="Document name (e.g., Deployment Letter, Contract, ID Copy)"
                        value={documentNames[guardKey] || ''}
                        onChange={(e) =>
                          setDocumentNames((prev) => ({ ...prev, [guardKey]: e.target.value }))
                        }
                        fullWidth
                        dark
                        helperText="Only PDF files are allowed"
                      />
                    </InputWrapper>
                    <ButtonWrapper>
                      <UploadButton htmlFor={fileInputId}>
                        <FiUpload size={18} />
                        {uploading[guard.id] ? 'Processing...' : 'Upload PDF'}
                        <input
                          id={fileInputId}
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleFileSelect(guard.id, file);
                            }
                            // Reset input
                            e.target.value = '';
                          }}
                          disabled={uploading[guard.id]}
                        />
                      </UploadButton>
                      {process.env.NODE_ENV === 'development' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handlePreviewDeploymentLetter(guard)}
                          disabled={uploading[guard.id]}
                          title="Preview Deployment Letter (development only)"
                        >
                          <FiEye size={16} />
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleGenerateDeploymentLetter(guard)}
                        disabled={uploading[guard.id]}
                      >
                        Generate Deployment Letter (PDF)
                      </Button>
                    </ButtonWrapper>
                  </FileInputContainer>
                </UploadSection>

                {guard.documents && guard.documents.length > 0 ? (
                  <DocumentsList>
                    {guard.documents.map((document) => (
                      <DocumentItem key={document.id}>
                        <DocumentInfo>
                          <DocumentIcon>
                            <FiFile size={20} />
                          </DocumentIcon>
                          <DocumentDetails>
                            <DocumentName>{document.name}</DocumentName>
                            <DocumentMeta>
                              {formatFileSize(document.fileSize)} • Uploaded{' '}
                              {formatDate(document.uploadedAt)}
                            </DocumentMeta>
                          </DocumentDetails>
                        </DocumentInfo>
                        <DocumentActions>
                          <ActionButton
                            onClick={() => {
                              setViewingPDF({
                                url: document.url,
                                name: document.name,
                                size: document.fileSize,
                                uploadedAt: document.uploadedAt,
                              });
                            }}
                            title="View Document"
                          >
                            <FiEye size={18} />
                          </ActionButton>
                          <ActionButton
                            onClick={() => window.open(document.url, '_blank')}
                            title="Download"
                          >
                            <FiDownload size={18} />
                          </ActionButton>
                          <ActionButton
                            className="delete"
                            onClick={() => handleDeleteDocument(guard.id, document.id)}
                            disabled={deleting[document.id]}
                            title="Delete"
                          >
                            <FiTrash2 size={18} />
                          </ActionButton>
                        </DocumentActions>
                      </DocumentItem>
                    ))}
                  </DocumentsList>
                ) : (
                  <EmptyState>No documents uploaded yet</EmptyState>
                )}
              </GuardSection>
            );
          })
        )}
      </ModalContent>

      {viewingPDF && (
        <PDFViewerModal
          isOpen={!!viewingPDF}
          onClose={() => {
            setViewingPDF(null);
            if (previewObjectUrl) {
              URL.revokeObjectURL(previewObjectUrl);
              setPreviewObjectUrl(null);
            }
          }}
          pdfUrl={viewingPDF.url}
          documentName={viewingPDF.name}
          documentSize={viewingPDF.size}
          uploadedAt={viewingPDF.uploadedAt}
        />
      )}
    </Modal>
  );
};

export default GuardDocumentsModal;

